import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import SlidePanel from "./SlidePanel";

const FILTERS = ["all", "active", "disabled"];

// Admin-only: list accounts and disable / re-enable them.
export default function UserAdmin({ API_BASE, account, onClose, setMessage }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setError("");
    fetch(`${API_BASE}/api/admin/users`, { headers: { Authorization: `Bearer ${Cookies.get("session")}` } })
      .then((r) => r.json())
      .then((d) => (Array.isArray(d) ? setUsers(d) : setError(d.error || "Failed to load users")))
      .catch(() => setError("Failed to load users"));
  }, [API_BASE]);

  useEffect(load, [load]);

  const setDisabled = async (u, disabled) => {
    if (disabled && !window.confirm(`Disable @${u.username}? They will be logged out and can't log in until you enable the account again. Their ships and edits stay.`)) return;
    setBusy(u.username);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(u.username)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session"), disabled }),
      });
      const d = await res.json();
      setMessage(d.message || d.error);
      if (!d.error) setUsers((list) => list.map((x) => (x.username === u.username ? { ...x, disabled } : x)));
    } catch {
      setMessage("Request failed.");
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: 0, active: 0, disabled: 0 };
    (users || []).forEach((u) => { c.all++; c[u.disabled ? "disabled" : "active"]++; });
    return c;
  }, [users]);

  const needle = q.trim().toLowerCase();
  const visible = (users || []).filter((u) =>
    (filter === "all" || (filter === "disabled") === u.disabled) &&
    (!needle || [u.username, u.email, u.name].filter(Boolean).join(" ").toLowerCase().includes(needle))
  );

  return (
    <SlidePanel
      title="Manage Users"
      subtitle="Disable accounts that break the rules"
      onClose={onClose}
      wide
      actions={<button className="panel-icon-btn" onClick={load} title="Refresh" aria-label="Refresh">↻</button>}
    >
      <div className="panel-tabs" role="tablist">
        {FILTERS.map((f) => (
          <button key={f} role="tab" aria-selected={filter === f} className={`panel-tab${filter === f ? " active" : ""}`} onClick={() => setFilter(f)}>
            {f[0].toUpperCase() + f.slice(1)} <span>{users ? counts[f] : "…"}</span>
          </button>
        ))}
      </div>
      <input className="panel-search" type="search" placeholder="Search username, name or email…" value={q} onChange={(e) => setQ(e.target.value)} />

      {error && <p className="panel-error">{error}</p>}
      {!error && users === null && <p className="panel-muted">Loading…</p>}
      {users && visible.length === 0 && <p className="panel-muted">No users match.</p>}

      <div className="panel-list">
        {visible.map((u) => {
          const self = u.username === account?.username;
          const locked = self || u.admin;
          return (
            <div key={u.id} className={`panel-item${u.disabled ? " user-disabled" : ""}`}>
              <div className="panel-item-main">
                <h3>@{u.username}</h3>
                <div className="panel-meta">
                  {u.admin && <span className="status-badge approved">Admin</span>}
                  {u.disabled && <span className="status-badge rejected">Disabled</span>}
                  <span>{u.ships} ship{u.ships === 1 ? "" : "s"}</span>
                  {u.country && <span>{u.country}</span>}
                </div>
                {u.email && <p className="panel-hint">{u.email}</p>}
              </div>
              <button
                className={`review-btn ${u.disabled ? "approve" : "reject"}`}
                disabled={busy === u.username || locked}
                title={self ? "You can't disable yourself" : u.admin ? "Admin accounts can't be disabled" : undefined}
                onClick={() => setDisabled(u, !u.disabled)}
              >
                {u.disabled ? "Enable" : "Disable"}
              </button>
            </div>
          );
        })}
      </div>
    </SlidePanel>
  );
}
